import { useEffect, useRef, useState } from 'react'
import { useTreeStore } from '../../store/useTreeStore'
import { useHandGesture } from '../Camera/HandGestureProvider'

// 检测是否为移动设备
const isMobile = () => {
  if (typeof window === 'undefined') return false
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768
}

export function UIOverlay() {
  const {
    treeState,
    progress,
    galleryProgress,
    isHandOpen,
    targetState,
    handCount,
  } = useTreeStore()
  const { isTracking, isLoading, canvasRef, startTracking, stopTracking } = useHandGesture()
  const previewRef = useRef<HTMLCanvasElement>(null)
  const [isMobileDevice, setIsMobileDevice] = useState(false)
  const [showCameraPanel, setShowCameraPanel] = useState(false) // 默认隐藏摄像头面板
  
  // 检测设备类型
  useEffect(() => {
    setIsMobileDevice(isMobile())
    const handleResize = () => setIsMobileDevice(isMobile())
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])
  
  // 将手势追踪画布内容复制到预览框 - 仅在非移动端且显示摄像头面板时执行
  useEffect(() => {
    // 移动端不需要手势追踪预览
    if (isMobileDevice) return
    if (!showCameraPanel) return
    if (!isTracking || !canvasRef.current || !previewRef.current) return
    
    const sourceCanvas = canvasRef.current
    const previewCanvas = previewRef.current
    const ctx = previewCanvas.getContext('2d')
    if (!ctx) return
    
    let animationId: number
    
    const copyFrame = () => {
      if (sourceCanvas.width > 0 && sourceCanvas.height > 0) {
        // 设置预览画布尺寸（保持比例）
        const aspectRatio = sourceCanvas.width / sourceCanvas.height
        const previewWidth = 240
        previewCanvas.width = previewWidth
        previewCanvas.height = previewWidth / aspectRatio
        
        // 镜像翻转绘制
        ctx.save()
        ctx.scale(-1, 1)
        ctx.drawImage(sourceCanvas, -previewCanvas.width, 0, previewCanvas.width, previewCanvas.height)
        ctx.restore()
      }
      animationId = requestAnimationFrame(copyFrame)
    }
    
    copyFrame()
    
    return () => {
      cancelAnimationFrame(animationId)
    }
  }, [isTracking, canvasRef, isMobileDevice, showCameraPanel])
  
  // 切换摄像头面板显示状态
  const toggleCameraPanel = () => {
    if (showCameraPanel) {
      // 关闭面板时停止追踪
      stopTracking()
      setShowCameraPanel(false)
    } else {
      // 打开面板时启动追踪
      setShowCameraPanel(true)
      startTracking()
    }
  }
  
  // 预览框尺寸 - 仅用于电脑端
  const previewWidth = 240
  const previewHeight = 180
  
  return (
    <div className="fixed inset-0 pointer-events-none z-10">
      {/* 顶部标题 - 画廊模式下显示在最上层 */}
      <div
        className={`absolute left-1/2 transform -translate-x-1/2 text-center ${isMobileDevice ? 'top-4' : 'top-8'}`}
        style={{ zIndex: galleryProgress > 0 ? 100 : 10 }}
      >
        <h1
          className={`font-serif drop-shadow-lg whitespace-nowrap ${isMobileDevice ? 'text-xl' : 'text-4xl md:text-6xl'}`}
          style={{
            color: '#FFD700',
            textShadow: '0 0 10px rgba(255, 215, 0, 0.8), 0 0 20px rgba(255, 215, 0, 0.6), 0 0 30px rgba(255, 215, 0, 0.4), 0 0 40px rgba(255, 215, 0, 0.2)',
            WebkitTextStroke: '1px rgba(255, 180, 0, 0.3)',
          }}
        >
          ✨ Merry Christmas ✨
        </h1>
        <p className={`text-white/80 font-light ${isMobileDevice ? 'text-xs mt-1' : 'text-lg md:text-xl mt-2'}`}>
          献给最特别的你
        </p>
      </div>
      
      {/* 电脑端：右上角摄像头开关按钮 */}
      {!isMobileDevice && (
        <div className="absolute top-4 right-4 pointer-events-auto">
          {/* 摄像头开关按钮 */}
          <button
            onClick={toggleCameraPanel}
            className={`rounded-full p-3 transition-all duration-300 transform hover:scale-105 active:scale-95 ${
              showCameraPanel ? 'ring-2 ring-white' : ''
            }`}
            style={{
              background: showCameraPanel 
                ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                : 'linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(20,20,20,0.8) 100%)',
              boxShadow: showCameraPanel 
                ? '0 0 20px rgba(34, 197, 94, 0.4), 0 4px 10px rgba(0, 0, 0, 0.3)'
                : '0 0 15px rgba(255, 215, 0, 0.2), 0 4px 10px rgba(0, 0, 0, 0.3)',
              border: '2px solid #ffd700',
            }}
            title={showCameraPanel ? '关闭手势控制' : '开启手势控制'}
          >
            <span className="text-xl">{showCameraPanel ? '📷' : '📷'}</span>
          </button>
          
          {/* 摄像头预览面板 - 仅在开启时显示 */}
          {showCameraPanel && (
            <div className="mt-2">
              {/* 摄像头预览框 - 金色边框 */}
              <div
                className="relative rounded-lg overflow-hidden transition-all duration-300"
                style={{
                  border: '2px solid #ffd700',
                  boxShadow: '0 0 15px rgba(255, 215, 0, 0.3), inset 0 0 8px rgba(0, 0, 0, 0.5)',
                  background: 'linear-gradient(135deg, rgba(0,0,0,0.8) 0%, rgba(20,20,20,0.9) 100%)',
                }}
              >
                {/* 预览画布 */}
                <canvas
                  ref={previewRef}
                  className="block"
                  style={{
                    width: `${previewWidth}px`,
                    height: `${previewHeight}px`,
                    display: isTracking ? 'block' : 'none',
                  }}
                />
                
                {/* 加载中或未启动时的占位内容 */}
                {!isTracking && (
                  <div
                    className="flex flex-col items-center justify-center text-center p-2"
                    style={{ width: `${previewWidth}px`, height: `${previewHeight}px` }}
                  >
                    {isLoading ? (
                      <>
                        <svg className="animate-spin text-gold mb-2 h-10 w-10" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <p className="text-white/70 text-sm">正在启动摄像头...</p>
                        <p className="text-white/50 text-xs mt-1">请允许摄像头权限</p>
                      </>
                    ) : (
                      <>
                        <div className="text-4xl mb-2">📷</div>
                        <p className="text-white/70 text-sm">正在初始化...</p>
                      </>
                    )}
                  </div>
                )}
                
                {/* 状态指示器 */}
                <div className="absolute flex items-center gap-2 bottom-2 left-2">
                  <span className={`rounded-full w-2 h-2 ${
                    isTracking
                      ? (targetState === 'GALLERY' ? 'bg-purple-400 animate-pulse' :
                         targetState === 'CHAOS' ? 'bg-green-400 animate-pulse' : 'bg-yellow-400')
                      : (isLoading ? 'bg-blue-400 animate-pulse' : 'bg-red-400')
                  }`} />
                  <span className="text-white/80 text-xs">
                    {isTracking
                      ? (handCount >= 2
                          ? '🙌 双手'
                          : (isHandOpen ? '✋ 张开' : '✊ 握拳'))
                      : (isLoading ? '⏳ 加载...' : '❌ 未连接')}
                  </span>
                </div>
                
                {/* 金色装饰角 */}
                <div className="absolute top-0 left-0 border-t-2 border-l-2 border-gold w-4 h-4" />
                <div className="absolute top-0 right-0 border-t-2 border-r-2 border-gold w-4 h-4" />
                <div className="absolute bottom-0 left-0 border-b-2 border-l-2 border-gold w-4 h-4" />
                <div className="absolute bottom-0 right-0 border-b-2 border-r-2 border-gold w-4 h-4" />
              </div>
              
              {/* 手势提示 - 仅在追踪时显示 */}
              {isTracking && (
                <div className="mt-1 bg-black/40 backdrop-blur-sm rounded-lg text-white/70 px-3 py-2 text-xs">
                  {targetState === 'FORMED' && (
                    <>
                      <div className="flex items-center gap-1 mb-0.5">
                        <span>👋</span>
                        <span>左右滑动旋转圣诞树</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span>✋</span>
                        <span>单手张开 → 展开模式</span>
                      </div>
                    </>
                  )}
                  {targetState === 'CHAOS' && (
                    <>
                      <div className="flex items-center gap-1 mb-0.5">
                        <span>👋</span>
                        <span>左右滑动拨动场景</span>
                      </div>
                      <div className="flex items-center gap-1 mb-0.5">
                        <span>✊</span>
                        <span>单手握拳 → 圣诞树</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span>🙌</span>
                        <span>双手张开 → 画廊模式</span>
                      </div>
                    </>
                  )}
                  {targetState === 'GALLERY' && (
                    <>
                      <div className="flex items-center gap-1 mb-0.5">
                        <span>👋</span>
                        <span>左右滑动切换照片</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span>✊✊</span>
                        <span>双手握拳 → 退出画廊</span>
                      </div>
                    </>
                  )}
                </div>
              )}
              
              {/* 当前状态显示 */}
              <div className="mt-1 bg-black/40 backdrop-blur-sm rounded-lg text-center px-3 py-2">
                <div className="flex items-center justify-center gap-1">
                  <span className={`rounded-full w-2 h-2 ${
                    targetState === 'FORMED' ? 'bg-green-400' :
                    targetState === 'CHAOS' ? 'bg-yellow-400' :
                    targetState === 'GALLERY' ? 'bg-purple-400' : 'bg-gray-400'
                  }`} />
                  <span className="text-white text-sm">
                    {targetState === 'FORMED' ? '🎄 圣诞树' :
                     targetState === 'CHAOS' ? '✨ 展开' :
                     targetState === 'GALLERY' ? '🖼️ 画廊' : '🔄 变形中...'}
                  </span>
                </div>
                {/* 进度条 */}
                {(treeState === 'TRANSITIONING' || galleryProgress > 0 && galleryProgress < 1) && (
                  <div className="mt-1">
                    <div className="w-full bg-white/20 rounded-full overflow-hidden h-1">
                      <div
                        className="h-full bg-gold transition-all duration-100"
                        style={{ width: `${targetState === 'GALLERY' ? galleryProgress * 100 : (1 - progress) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* 底部署名 */}
      <div className={`absolute text-white/40 text-xs ${isMobileDevice ? 'bottom-2 right-2' : 'bottom-4 right-4'}`}>
        Made with ❤️ for you
      </div>
    </div>
  )
}